/**
 * BotonRecibo.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Botón reutilizable: genera el PDF del recibo en el cliente (sin servidor),
 * lo descarga automáticamente al navegador y opcionalmente lo guarda en el
 * servidor para su resguardo.
 *
 * Uso:
 *   <BotonRecibo evento={eventoObject} />
 *   <BotonRecibo evento={eventoObject} variant="icon" />     ← solo ícono
 *   <BotonRecibo evento={eventoObject} guardar={false} />    ← solo descarga local
 * ─────────────────────────────────────────────────────────────────────────────
 */
import React, { useState } from "react";
import { pdf } from "@react-pdf/renderer";
import {
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import { PictureAsPdf as PdfIcon } from "@mui/icons-material";
import ReciboPDF from "./ReciboPDF";
import api from "../services/api";
import logoSrc from "../images/R1.webp";

const folioStr = (id) => String(id).padStart(4, "0");

/** Convierte cualquier imagen (webp, etc.) a data URL PNG mediante canvas */
const toDataUrlPng = (src) =>
  new Promise((resolve) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width  = img.naturalWidth  || img.width;
        canvas.height = img.naturalHeight || img.height;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

const BotonRecibo = ({
  evento,
  variant = "button",   // "button" | "icon"
  size    = "small",
  guardar = true,       // true  → sube al servidor además de descargar
  label   = "Recibo PDF",
  disabled = false,     // deshabilitar externamente
  disabledTooltip = "No disponible", // texto al hacer hover cuando está deshabilitado
  sx      = {},
}) => {
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

  const handleGenerar = async () => {
    if (!evento) return;
    setLoading(true);

    try {
      // 0. RECARGA el evento completo con TODOS los datos incluyendo servicios
      let eventoCompleto = evento;
      if (evento.id) {
        try {
          const response = await api.get(
            `/eventos/${evento.id}?include=cliente,colaboradores,servicios,paquetes,insumos,gastos`
          );
          eventoCompleto = response.data?.evento || response.data || evento;
          console.log("[BotonRecibo] Evento recargado con servicios:", eventoCompleto);
        } catch (err) {
          console.warn("[BotonRecibo] No se pudo recargar evento, usando local:", err?.message);
          eventoCompleto = evento;
        }
      }

      // 1. Convertir logo webp a PNG data URL (react-pdf no soporta webp)
      const logoUrl = await toDataUrlPng(logoSrc);

      // 2. Generar el blob del PDF en el navegador
      const blob = await pdf(<ReciboPDF evento={eventoCompleto} logoUrl={logoUrl} />).toBlob();

      // 3. Descarga inmediata al navegador del usuario
      const url      = URL.createObjectURL(blob);
      const anchor   = document.createElement("a");
      anchor.href    = url;
      anchor.download = `Recibo-${folioStr(evento.id)}-${(
        evento.cliente_nombre || "Cliente"
      )
        .replace(/\s+/g, "_")
        .replace(/[^a-zA-Z0-9_-]/g, "")}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);

      // 4. Guardar en servidor (en paralelo, no bloquea)
      if (guardar) {
        const reader  = new FileReader();
        reader.onloadend = async () => {
          try {
            // reader.result = "data:application/pdf;base64,XXXX"
            const base64 = reader.result.split(",")[1];
            await api.post("/recibos", {
              evento_id:   evento.id,
              pdf_base64:  base64,
            });
          } catch (err) {
            // El guardado en servidor es secundario; no interrumpir al usuario
            console.warn("[BotonRecibo] No se pudo guardar en servidor:", err?.message);
          }
        };
        reader.readAsDataURL(blob);
      }

      setSnack({ open: true, msg: "✓ Recibo generado y descargado", sev: "success" });
    } catch (err) {
      console.error("[BotonRecibo] Error al generar PDF:", err);
      setSnack({ open: true, msg: "Error al generar el recibo", sev: "error" });
    } finally {
      setLoading(false);
    }
  };

  const icon = loading ? (
    <CircularProgress size={16} sx={{ color: "inherit" }} />
  ) : (
    <PdfIcon fontSize={size === "large" ? "medium" : "small"} />
  );

  return (
    <>
      {variant === "icon" ? (
        <Tooltip title={disabled ? disabledTooltip : label}>
          <span>
            <IconButton
              size={size}
              onClick={handleGenerar}
              disabled={loading || !evento || disabled}
              sx={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: disabled
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(232,112,87,0.18)",
                color: disabled ? "action.disabled" : "#E87057",
                transition: "all 0.2s",
                "&:hover": {
                  bgcolor: disabled ? undefined : "rgba(232,112,87,0.32)",
                  transform: disabled ? undefined : "scale(1.1)",
                },
                ...sx,
              }}
            >
              {icon}
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Tooltip title={disabled ? disabledTooltip : "Generar y descargar recibo en PDF"}>
          <span>
            <Button
              variant="outlined"
              size={size}
              startIcon={icon}
              onClick={handleGenerar}
              disabled={loading || !evento || disabled}
              sx={{
                borderColor: disabled ? "action.disabled" : "#E87057",
                color: disabled ? "text.disabled" : "#E87057",
                "&:hover": {
                  borderColor: disabled ? undefined : "#C4573F",
                  bgcolor: disabled ? undefined : "rgba(232,112,87,0.08)",
                },
                fontWeight: 600,
                letterSpacing: 0.5,
                ...sx,
              }}
            >
              {loading ? "Generando..." : label}
            </Button>
          </span>
        </Tooltip>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.sev}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BotonRecibo;
